import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PWABanner from "@/components/PWABanner";
import { SeloDestaque } from "@/components/SeloDestaque";
import {
  Zap, Star, Wallet, Briefcase,
  MapPin, Package, Clock, AlertTriangle,
  CheckCircle2, ArrowRight, Timer,
  MessageSquare, Calendar as CalIcon, Receipt, Settings as CogIcon, FileSignature as FSIcon
} from "lucide-react";

export default function MontadorPainel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [servicos, setServicos] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [extrato, setExtrato] = useState(null);
  const [dash, setDash] = useState(null);
  const [ass, setAss] = useState(null);
  const [tab, setTab] = useState("ofertas");
  const [meuScore, setMeuScore] = useState(null);
  const [campanhas, setCampanhas] = useState([]);
  const [agora, setAgora] = useState(Date.now());

  const load = () => {
    api.get("/servicos").then(r => setServicos(r.data));
    api.get("/montador/saldo").then(r => setSaldo(r.data)).catch(() => {});
    api.get("/montador/extrato").then(r => setExtrato(r.data)).catch(() => {});
    api.get("/montador/dashboard").then(r => setDash(r.data)).catch(() => {});
    api.get("/me/assinatura").then(r => setAss(r.data)).catch(() => {});
    api.get("/montador/meu-score").then(r => setMeuScore(r.data)).catch(() => {});
    api.get("/montador/campanhas").then(r => setCampanhas(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const poll = setInterval(() => api.get("/servicos").then(r => setServicos(r.data)).catch(() => {}), 12000);
    const tick = setInterval(() => setAgora(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const aceitar = async (sid) => {
    try {
      await api.post(`/servicos/${sid}/aceitar`);
      toast.success("Serviço aceito! Defina 3 datas.");
      navigate(`/montador/servico/${sid}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const interesse = async (sid) => {
    try {
      const r = await api.post(`/servicos/${sid}/interesse`);
      toast.success(r.data?.mensagem || "Interesse registrado!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmarSelecao = async (sid) => {
    try {
      await api.post(`/servicos/${sid}/confirmar-selecao`);
      toast.success("Atendimento confirmado! Defina 3 datas.");
      navigate(`/montador/servico/${sid}`);
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const recusarSelecao = async (sid) => {
    try {
      await api.post(`/servicos/${sid}/recusar-selecao`);
      toast.success("Sem problema — isso não afeta seu score.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const liberarGrupo = async (sid) => {
    try {
      await api.post(`/servicos/${sid}/liberar-agrupamento`);
      toast.success("Liberado para a rede — sem prejuízo ao seu score.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ofertas = servicos.filter(s =>
    ["aguardando_aceite_tecnico", "em_microjanela"].includes(s.status) ||
    (s.status === "aguardando_confirmacao" && s.sou_selecionado) ||
    (s.status === "reservada_para_agrupamento" && s.grupo_montador_id === user?.montador_id));
  const meus = servicos.filter(s => s.montador_id === user?.montador_id);
  const ativos = meus.filter(s => !["finalizado", "cancelado"].includes(s.status));
  const finalizados = meus.filter(s => s.status === "finalizado");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
          Painel do Montador
        </div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">
          Suas oportunidades e ganhos
        </h1>
      </div>

      <PWABanner />
      {ass && <AssinaturaBanner ass={ass} />}

      <DisponivelAgora />

      {/* 4 KPI Cards principais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="mont-kpi-ofertas"
          icon={<Zap className="w-5 h-5" />}
          label="Oportunidades"
          value={ofertas.length}
          hint="Disponíveis para aceite"
          color="orange"
          pulse={ofertas.length > 0}
        />
        <KpiCard
          testid="mont-kpi-ativos"
          icon={<Briefcase className="w-5 h-5" />}
          label="Serviços Ativos"
          value={ativos.length}
          hint={`${finalizados.length} finalizados`}
          color="blue"
        />
        <KpiCard
          testid="mont-kpi-score"
          icon={<Star className="w-5 h-5" />}
          label="Score Pessoal"
          value={dash?.score?.toFixed?.(0) ?? "—"}
          hint={`★ ${dash?.nota_media || "—"} · ${dash?.total_avaliacoes || 0} avaliações`}
          color="orange"
        />
        <KpiCard
          testid="mont-kpi-saldo"
          icon={<Wallet className="w-5 h-5" />}
          label="Recebimento Previsto"
          value={saldo ? `R$ ${Number(saldo.saldo).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"}
          hint={saldo ? `Período ${saldo.periodo}` : ""}
          color="blue"
        />
      </div>

      {/* Extrato — a receber por empresa */}
      {meuScore?.detalhes && (
        <div className="aj-card p-5" data-testid="meu-score-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Meu Score MontaJá</div>
              <div className="font-display font-extrabold text-xl text-aj-blue-deep">
                {Number(meuScore.score ?? 0).toFixed(0)} <span className="text-sm font-normal text-aj-text-muted">/ 100</span>
              </div>
            </div>
            <span className="inline-flex items-center gap-2">
              <SeloDestaque show={meuScore.destaque} />
              <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-[#DBEAFE] text-[#142766]"
                data-testid="meu-nivel-experiencia">{meuScore.nivel_experiencia}</span>
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {[["Qualidade", "qualidade", 35], ["Pontualidade", "pontualidade", 20], ["Execução", "execucao", 15],
              ["Avaliação", "avaliacao", 15], ["Compromisso", "compromisso", 10], ["Confiabilidade", "confiabilidade", 5]]
              .map(([lbl, k, peso]) => (
                <div key={k} className="border border-aj-border rounded-lg p-2 text-center" data-testid={`score-comp-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider text-aj-text-muted">{lbl} <span className="text-aj-orange">{peso}%</span></div>
                  <div className="font-mono font-bold text-sm text-aj-blue-deep">{Number(meuScore.detalhes[k] ?? 0).toFixed(0)}</div>
                </div>
              ))}
          </div>
          <div className="mt-2 text-[11px] text-aj-text-muted">
            Recusar ofertas ou fechar a agenda <b>nunca</b> reduz seu score. Retrabalhos só contam após classificação de causa.
          </div>
        </div>
      )}

      {extrato?.empresas?.length > 0 && (
        <div className="aj-card p-5" data-testid="extrato-montador">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 min-w-0">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Extrato · A Receber por Empresa</div>
              <div className="font-display font-extrabold text-xl text-aj-blue-deep break-words">
                R$ {Number(extrato.total_a_receber).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </div>
            </div>
            <Link to="/montador/valores-empresas" className="text-xs font-semibold text-aj-orange hover:underline shrink-0"
              data-testid="extrato-ver-valores">Ver valores das empresas →</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3 min-w-0">
            {extrato.empresas.map(e => (
              <div key={e.empresa_id} className="border border-aj-border rounded-lg p-4 min-w-0" data-testid={`extrato-emp-${e.empresa_id}`}>
                <div className="font-display font-bold text-sm text-aj-blue-deep break-words">{e.empresa_nome}</div>
                <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 min-w-0">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-aj-text-muted">A receber</div>
                    <div className="font-mono font-bold text-lg text-aj-success break-words">
                      R$ {Number(e.a_receber).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-[11px] text-aj-text-muted">{e.servicos} serviço(s) pendente(s)</div>
                  </div>
                  <div className="sm:text-right min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-aj-text-muted">Próximo pagamento</div>
                    <div className="text-sm font-semibold text-aj-blue-deep">
                      {e.proximo_pagamento
                        ? new Date(e.proximo_pagamento + "T12:00:00").toLocaleDateString("pt-BR")
                        : "A definir"}
                    </div>
                    {e.periodicidade && (
                      <div className="text-[11px] text-aj-text-muted capitalize break-words">{e.periodicidade} · {e.forma_pagamento || "PIX"}{e.dia_fechamento ? ` · fecha dia ${e.dia_fechamento}` : ""}</div>
                    )}
                  </div>
                </div>
                {e.itens?.length > 0 && (
                  <div className="mt-3 border-t border-aj-border pt-2 space-y-1 min-w-0" data-testid={`extrato-itens-${e.empresa_id}`}>
                    {e.itens.map((i, ix) => (
                      <div key={ix} className="flex items-center justify-between gap-2 text-[11px] min-w-0">
                        <span className="truncate min-w-0 text-aj-text-muted">
                          <b className="font-mono text-aj-blue-deep">{i.pim_code}</b> · {i.produto || i.tipo}
                        </span>
                        <span className="font-mono font-bold text-aj-success whitespace-nowrap">R$ {Number(i.valor).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerta de faltas */}
      {dash?.faltas_nao_justificadas_90d > 0 && (
        <div className="rounded-lg border-l-4 border-aj-danger bg-red-50 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-aj-danger" />
          <div>
            <div className="font-display font-bold text-sm text-aj-danger">
              {dash.faltas_nao_justificadas_90d} falta(s) sem justificativa nos últimos 90 dias
            </div>
            <div className="text-xs text-aj-text-muted">Regularize com sua Montadora para evitar bloqueio de oportunidades.</div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <QuickAction to="/montador/agenda" icon={<CalIcon className="w-4 h-4" />} label="Agenda" testid="qa-agenda" />
        <QuickAction to="/admin/chat" icon={<MessageSquare className="w-4 h-4" />} label="Chat Montadora" testid="qa-chat" />
        <QuickAction to="/montador/notas-fiscais" icon={<Receipt className="w-4 h-4" />} label="Minhas NFs" testid="qa-nf" />
        <QuickAction to="/montador/aditivos" icon={<FSIcon className="w-4 h-4" />} label="Aditivos" testid="qa-aditivos" />
        <QuickAction to="/montador/perfil-fiscal" icon={<FSIcon className="w-4 h-4" />} label="Perfil Fiscal" testid="qa-perfil-fiscal" />
        <QuickAction to="/montador/configuracoes" icon={<CogIcon className="w-4 h-4" />} label="Configurações" testid="qa-config-op" />
      </div>

      {/* Campanhas de incentivo ativas na região */}
      {campanhas.length > 0 && (
        <div className="aj-card p-4 border-l-4 border-l-[#00A859]" data-testid="montador-campanhas">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#00A859] mb-2">
            🎯 Campanhas de incentivo ativas na sua região
          </div>
          <div className="space-y-1.5">
            {campanhas.map(c => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`campanha-${c.id}`}>
                <span className="font-bold text-aj-blue-deep">{c.nome}</span>
                <span className="px-2 py-0.5 rounded bg-[#00A859] text-white text-[11px] font-bold">
                  +{c.modo_incentivo === "fixo" ? `R$ ${Number(c.valor_incentivo).toFixed(2)}` : `${c.valor_incentivo}%`} por atendimento
                </span>
                <span className="text-[11px] text-aj-text-muted">
                  {c.empresa_nome} · {c.cidade || "todas as cidades"}{c.estado ? `/${c.estado}` : ""} · até {c.data_fim}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs de serviços */}
      <div className="aj-card p-1 inline-flex bg-white border-aj-border">
        <TabBtn active={tab === "ofertas"} onClick={() => setTab("ofertas")} testid="tab-ofertas">
          Ofertas <span className="ml-1 font-bold">({ofertas.length})</span>
        </TabBtn>
        <TabBtn active={tab === "ativos"} onClick={() => setTab("ativos")} testid="tab-ativos">
          Meus serviços <span className="ml-1 font-bold">({ativos.length})</span>
        </TabBtn>
        <TabBtn active={tab === "finalizados"} onClick={() => setTab("finalizados")} testid="tab-finalizados">
          Finalizados <span className="ml-1 font-bold">({finalizados.length})</span>
        </TabBtn>
      </div>

      {tab === "ofertas" && (
        <div className="space-y-3">
          {ofertas.length === 0
            ? <EmptyState msg="Nenhuma oferta disponível no momento. Vamos avisar quando aparecer!" />
            : ofertas.map(s => (
              <OportunidadeCard key={s.id} s={s} agora={agora}
                onAceitar={() => aceitar(s.id)}
                onInteresse={() => interesse(s.id)}
                onConfirmar={() => confirmarSelecao(s.id)}
                onRecusarSelecao={() => recusarSelecao(s.id)}
                onLiberarGrupo={() => liberarGrupo(s.id)} />
            ))
          }
        </div>
      )}
      {tab === "ativos" && (
        <div className="space-y-3">
          {ativos.length === 0
            ? <EmptyState msg="Nenhum serviço em andamento." />
            : ativos.map(s => <ServicoCard key={s.id} s={s} to={`/montador/servico/${s.id}`} action="Abrir" />)
          }
        </div>
      )}
      {tab === "finalizados" && (
        <div className="space-y-3">
          {finalizados.length === 0
            ? <EmptyState msg="Nenhum serviço finalizado ainda." />
            : finalizados.map(s => <ServicoCard key={s.id} s={s} to={`/montador/servico/${s.id}`} action="Ver detalhes" />)
          }
        </div>
      )}
    </div>
  );
}

function KpiCard({ testid, icon, label, value, hint, color = "orange", pulse }) {
  return (
    <div className={`aj-card p-5 ${pulse ? "ring-2 ring-aj-orange animate-pulse-slow" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className={color === "blue" ? "aj-icon-tile-blue w-10 h-10" : "aj-icon-tile w-10 h-10"} style={{ width: 40, height: 40 }}>
          {icon}
        </div>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted leading-tight">{label}</div>
      <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-1">{value}</div>
      {hint && <div className="text-[11px] text-aj-text-muted mt-0.5">{hint}</div>}
    </div>
  );
}

function QuickAction({ to, icon, label, testid }) {
  return (
    <Link to={to} className="aj-card p-4 flex flex-col items-center text-center gap-1.5 hover:border-aj-orange transition" data-testid={testid}>
      <div className="aj-icon-tile-soft">{icon}</div>
      <div className="text-xs font-semibold text-aj-blue-deep">{label}</div>
    </Link>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold rounded transition ${
        active ? "bg-aj-orange text-white" : "text-aj-text-muted hover:text-aj-blue-deep"
      }`}>
      {children}
    </button>
  );
}

function fmtTempoMin(min) {
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h${m ? String(m).padStart(2, "0") : ""}` : `${m}min`;
}

function OportunidadeCard({ s, agora, onAceitar, onInteresse, onConfirmar, onRecusarSelecao, onLiberarGrupo }) {
  const emMicrojanela = s.status === "em_microjanela";
  const souSelecionado = s.status === "aguardando_confirmacao" && s.sou_selecionado;
  const reservada = s.status === "reservada_para_agrupamento";
  const restMj = emMicrojanela && s.microjanela_fim
    ? Math.max(0, Math.round((new Date(s.microjanela_fim).getTime() - agora) / 1000)) : 0;
  const restConf = souSelecionado && s.confirmacao_fim
    ? Math.max(0, Math.round((new Date(s.confirmacao_fim).getTime() - agora) / 1000)) : 0;

  return (
    <div className={`aj-card overflow-hidden ${souSelecionado ? "ring-2 ring-aj-orange" : ""}`} data-testid={`oportunidade-${s.id}`}>
      {/* Banner de destaque */}
      <div className="aj-band-orange px-5 py-2 flex items-center justify-between text-white text-xs font-bold uppercase tracking-wider">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" />
          {reservada ? "Mesmo endereço — reservado para você" : souSelecionado ? "Você foi selecionado!" : emMicrojanela ? "Janela de interesse aberta" : "Nova oportunidade"}
        </div>
        {emMicrojanela && restMj > 0 && (
          <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded" data-testid={`mj-countdown-${s.id}`}>
            <Timer className="w-3 h-3" /> {restMj}s
          </div>
        )}
        {souSelecionado && restConf > 0 && (
          <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded animate-pulse" data-testid={`conf-countdown-${s.id}`}>
            <Timer className="w-3 h-3" /> Confirme em {restConf}s
          </div>
        )}
        {s.urgente_juridico && (
          <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
            <Timer className="w-3 h-3" /> Urgente
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[11px] text-aj-text-muted">{s.pim_code}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-aj-surface text-aj-blue-deep px-1.5 py-0.5 rounded">{s.tipo}</span>
            </div>
            <div className="font-display font-extrabold text-lg text-aj-blue-deep">
              {s.produto} <span className="text-aj-text-muted font-normal text-sm">({s.quantidade})</span>
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-aj-text">
                <MapPin className="w-3.5 h-3.5 text-aj-orange" />
                <span>{s.cliente_cidade}{s.cliente_estado ? `/${s.cliente_estado}` : ""} · {s.cliente_bairro || "Bairro após aceite"}</span>
              </div>
              {s.tempo_estimado_min && (
                <div className="flex items-center gap-2 text-aj-text">
                  <Clock className="w-3.5 h-3.5 text-aj-orange" />
                  <span>Previsão: ~{fmtTempoMin(s.tempo_estimado_min)} <span className="text-[11px] text-aj-text-muted">(referência, não é meta)</span></span>
                </div>
              )}
              {s.cor && (
                <div className="flex items-center gap-2 text-aj-text">
                  <Package className="w-3.5 h-3.5 text-aj-orange" /> Cor: {s.cor}
                </div>
              )}
              {souSelecionado && (
                <div className="flex items-center gap-2 text-aj-success font-semibold" data-testid={`valor-selecionado-${s.id}`}>
                  <Wallet className="w-3.5 h-3.5" />
                  {s.valor_oferta_selecionado
                    ? `Você recebe R$ ${Number(s.valor_oferta_selecionado).toFixed(2)}`
                    : "Valor pela tabela vigente da empresa"}
                </div>
              )}
              {!souSelecionado && s.valor_montador && (
                <div className="flex items-center gap-2 text-aj-success font-semibold">
                  <Wallet className="w-3.5 h-3.5" /> Você recebe R$ {Number(s.valor_montador).toFixed(2)}
                </div>
              )}
            </div>
            {s.exige_nota_fiscal && (
              <div className="mt-3 rounded-lg border-2 border-[#E60000] bg-[#FEF2F2] p-3" data-testid={`aviso-nf-${s.id}`}>
                <div className="text-xs font-extrabold uppercase tracking-wide text-[#B91C1C]">
                  ⚠️ Atenção: pagamento somente com NOTA FISCAL
                </div>
                <div className="text-[11px] text-[#991B1B] mt-1 leading-snug">
                  Esta empresa <strong>só paga mediante nota fiscal</strong> (exige CNPJ ativo).
                  A NF deve ser enviada em até <strong>2 dias após o fechamento</strong> pelo painel
                  "Notas Fiscais". Fora do prazo, o valor acumula para o próximo fechamento.
                </div>
              </div>
            )}
            <div className="mt-3 text-[11px] text-aj-text-muted italic">
              🔒 CPF, endereço completo e contato liberados após {souSelecionado ? "a confirmação" : "aceitar"}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-end">
          {reservada && (
            <>
              <button onClick={onLiberarGrupo}
                className="btn-aj-outline text-sm justify-center"
                data-testid={`liberar-grupo-${s.id}`}>
                Liberar para a rede
              </button>
              <button onClick={onAceitar}
                className="btn-aj-primary text-sm justify-center"
                data-testid={`aceitar-grupo-${s.id}`}>
                Aceitar (mesmo atendimento) <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          {souSelecionado && (
            <>
              <button onClick={onRecusarSelecao}
                className="btn-aj-outline text-sm justify-center"
                data-testid={`recusar-selecao-${s.id}`}>
                Não posso (sem prejuízo)
              </button>
              <button onClick={onConfirmar}
                className="btn-aj-primary text-sm justify-center"
                data-testid={`confirmar-selecao-${s.id}`}>
                Confirmar atendimento <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          {emMicrojanela && !s.meu_interesse && restMj > 0 && (
            <button onClick={onInteresse}
              className="btn-aj-primary text-sm justify-center"
              data-testid={`interesse-${s.id}`}>
              ⚡ Tenho interesse
            </button>
          )}
          {emMicrojanela && s.meu_interesse && (
            <div className="text-sm font-semibold text-aj-blue-deep bg-aj-surface rounded-lg px-4 py-2 text-center"
              data-testid={`interesse-registrado-${s.id}`}>
              ✓ Interesse registrado — a seleção considera seu Score
            </div>
          )}
          {emMicrojanela && !s.meu_interesse && restMj === 0 && (
            <div className="text-xs text-aj-text-muted px-4 py-2">Janela encerrada — processando seleção…</div>
          )}
          {s.status === "aguardando_aceite_tecnico" && (
            <button onClick={onAceitar}
              className="btn-aj-primary text-sm justify-center"
              data-testid={`aceitar-${s.id}`}>
              Aceitar Cegamente <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ServicoCard({ s, to, action }) {
  const statusColor = {
    finalizado: "bg-green-100 text-green-700",
    cancelado: "bg-red-100 text-red-700",
  }[s.status] || "bg-aj-amber-soft text-aj-orange-hover";

  return (
    <div className="aj-card p-5" data-testid={`servico-${s.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-mono text-[11px] text-aj-text-muted">{s.pim_code}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusColor}`}>
              {s.status?.replace(/_/g, " ")}
            </span>
            {s.urgente_juridico && <span className="w-2 h-2 rounded-full bg-aj-danger animate-pulse" title="Urgente" />}
          </div>
          <div className="font-display font-bold text-base text-aj-blue-deep">{s.cliente_nome}</div>
          <div className="text-xs text-aj-text-muted mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {s.cliente_endereco} · {s.cliente_cidade}/{s.cliente_estado}
          </div>
          <div className="mt-2 text-sm">
            <strong>{s.produto}</strong>
            <span className="text-aj-text-muted"> ({s.quantidade}){s.cor && ` · ${s.cor}`}</span>
          </div>
          {s.data_confirmada && (
            <div className="mt-2 flex items-center gap-1 text-xs font-bold text-aj-success">
              <CheckCircle2 className="w-3.5 h-3.5" /> Agendado para {s.data_confirmada}
            </div>
          )}
        </div>
        <Link to={to} className="btn-aj-outline text-xs whitespace-nowrap self-center" data-testid={`abrir-${s.id}`}>
          {action} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="aj-card p-10 text-center border-dashed">
      <Clock className="w-8 h-8 mx-auto mb-3 text-aj-text-muted" />
      <div className="text-sm text-aj-text-muted">{msg}</div>
    </div>
  );
}

function AssinaturaBanner({ ass }) {
  if (!ass || !ass.status_assinatura || ass.status_assinatura === "n/a") return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
  const palette = {
    aguardando_aprovacao: { border: "border-aj-orange", bg: "bg-aj-amber-soft" },
    gratuito: { border: "border-aj-success", bg: "bg-green-50" },
    ativo: { border: "border-aj-blue", bg: "bg-blue-50" },
    pendente: { border: "border-aj-orange", bg: "bg-aj-amber-soft" },
    suspenso: { border: "border-aj-danger", bg: "bg-red-50" },
  }[ass.status_assinatura] || { border: "border-aj-border", bg: "bg-aj-surface" };
  const msgs = {
    aguardando_aprovacao: "Aguardando aprovação do admin. Após aprovação, 6 meses gratuitos.",
    gratuito: `Plataforma gratuita até ${fmt(ass.data_fim_gratuidade)}. Depois: R$ ${ass.valor_mensalidade?.toFixed(2) || "—"}/mês.`,
    ativo: `Assinatura ativa · Próx. cobrança ${fmt(ass.proxima_cobranca)}`,
    pendente: `Mensalidade pendente. Próx. cobrança: ${fmt(ass.proxima_cobranca)}.`,
    suspenso: "Conta suspensa. Regularize para reativar.",
  };
  return (
    <div className={`rounded-lg border-l-4 ${palette.border} ${palette.bg} p-4`} data-testid={`banner-${ass.status_assinatura}`}>
      <div className="font-display font-bold text-sm text-aj-blue-deep capitalize">
        Assinatura: {ass.status_assinatura.replace(/_/g, " ")}
      </div>
      <div className="text-xs text-aj-text-muted mt-1">{msgs[ass.status_assinatura]}</div>
    </div>
  );
}

function DisponivelAgora() {
  const [disp, setDisp] = useState(null);
  const [sel, setSel] = useState("2h");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/montador/disponivel-agora").then(r => setDisp(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const ativar = async () => {
    setBusy(true);
    try {
      await api.post("/montador/disponivel-agora", { duracao: sel });
      toast.success("Disponibilidade ativada!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const cancelar = async () => {
    setBusy(true);
    try {
      await api.delete("/montador/disponivel-agora");
      toast.success("Disponibilidade cancelada.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const ateHM = disp?.disponivel_ate
    ? new Date(disp.disponivel_ate).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : null;
  const opcoes = [["2h", "2 horas"], ["4h", "4 horas"], ["fim_do_dia", "Até o fim do dia"]];

  return (
    <div className="aj-card p-4" data-testid="disponivel-agora-card">
      {disp?.ativo ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
            <div>
              <div className="font-display font-extrabold text-aj-blue-deep" data-testid="disponivel-ate-label">
                Disponível até {ateHM}
              </div>
              <div className="text-[11px] text-aj-text-muted">
                Sinalização voluntária — as regras normais de distribuição continuam valendo.
              </div>
            </div>
          </div>
          <button onClick={cancelar} disabled={busy}
            className="text-xs font-semibold text-aj-danger border border-aj-danger/30 rounded-lg px-3 py-2 hover:bg-red-50 transition-colors"
            data-testid="disponivel-cancelar-btn">
            Cancelar disponibilidade
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display font-extrabold text-aj-blue-deep">Estou disponível agora</div>
            <div className="text-[11px] text-aj-text-muted">
              Avise a operação que você está livre neste momento (não altera prioridade nem regras).
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {opcoes.map(([v, label]) => (
              <button key={v} onClick={() => setSel(v)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                  sel === v ? "bg-aj-blue-deep text-white border-aj-blue-deep" : "border-[#E2E8F0] text-aj-text hover:bg-aj-surface"
                }`}
                data-testid={`disponivel-opcao-${v}`}>
                {label}
              </button>
            ))}
            <button onClick={ativar} disabled={busy} className="btn-aj-primary text-xs"
              data-testid="disponivel-ativar-btn">
              Ativar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
